// Contagem de prazos processuais. Datas sempre no formato 'AAAA-MM-DD', sem fuso.
// Regras: exclui o dia do começo e inicia no primeiro dia útil seguinte (CPC 224 e CPP 798, § 1º);
// publicação no primeiro dia útil após a disponibilização (CPC 224, § 2º);
// recesso de 20/12 a 20/01 (CPC 220); vencimento em dia sem expediente passa para o próximo útil.
import { courtGroupOf, officialCalendar } from './courtCalendars';

export type DeadlineMode = 'uteis' | 'corridos';
export type StartKind = 'disponibilizacao' | 'publicacao';

export interface NonWorkingDay {
  date: string;
  reason: string;
}

export interface DeadlineInput {
  baseDate: string;
  startKind: StartKind;
  days: number;
  mode: DeadlineMode;
  doubled?: boolean;
  court: string;
  capitalRecife?: boolean;
  suspendInRecess?: boolean;
  customDays?: NonWorkingDay[];
}

export interface CalendarUsed {
  year: number;
  source: string | null;
}

export interface DeadlineResult {
  publication: string;
  start: string;
  end: string;
  countedDays: number;
  skipped: NonWorkingDay[];
  movedEndFrom: string | null;
  calendars: CalendarUsed[];
}

const toTime = (date: string) => {
  const [y, m, d] = date.split('-').map(Number);
  return Date.UTC(y, m - 1, d);
};
const fromTime = (time: number) => new Date(time).toISOString().slice(0, 10);
export const addDays = (date: string, amount: number) => fromTime(toTime(date) + amount * 86400000);
const weekDay = (date: string) => new Date(toTime(date)).getUTCDay();
export const formatBr = (date: string) => date.split('-').reverse().join('/');
export const isValidDate = (date: string) => /^\d{4}-\d{2}-\d{2}$/.test(date) && fromTime(toTime(date)) === date;

// Algoritmo de Meeus/Jones/Butcher
const easter = (year: number) => {
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31);
  const day = ((h + l - 7 * m + 114) % 31) + 1;
  return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
};

/**
 * Dias sem expediente do tribunal no ano. Sem calendário oficial cadastrado, usa só o que a lei nacional
 * garante: assim um dia que o tribunal não tenha fechado nunca empurra o vencimento para depois do correto.
 */
export function holidaysOfYear(year: number, court: string, capitalRecife = false): NonWorkingDay[] {
  const official = officialCalendar(court, year);
  if (official) {
    return official.days.filter((d) => !d.recifeOnly || capitalRecife).map(({ date, reason }) => ({ date, reason }));
  }
  const fixed = (md: string, reason: string) => ({ date: `${year}-${md}`, reason });
  const pascoa = easter(year);
  const list: NonWorkingDay[] = [
    fixed('01-01', 'Confraternização Universal'),
    fixed('04-21', 'Tiradentes'),
    fixed('05-01', 'Dia do Trabalho'),
    fixed('09-07', 'Independência'),
    fixed('10-12', 'Nossa Senhora Aparecida'),
    fixed('11-02', 'Finados'),
    fixed('11-15', 'Proclamação da República'),
    fixed('11-20', 'Consciência Negra'),
    fixed('12-25', 'Natal'),
    { date: addDays(pascoa, -2), reason: 'Sexta-feira Santa' },
  ];
  if (courtGroupOf(court) === 'Justiça Federal') {
    // Lei 5.010/66, art. 62
    list.push(
      { date: addDays(pascoa, -48), reason: 'Carnaval (Lei 5.010/66)' },
      { date: addDays(pascoa, -47), reason: 'Carnaval (Lei 5.010/66)' },
      { date: addDays(pascoa, -4), reason: 'Semana Santa (Lei 5.010/66)' },
      { date: addDays(pascoa, -3), reason: 'Semana Santa (Lei 5.010/66)' },
      fixed('08-11', 'Cursos Jurídicos (Lei 5.010/66)'),
      fixed('11-01', 'Todos os Santos (Lei 5.010/66)'),
      fixed('12-08', 'Dia da Justiça (Lei 5.010/66)'),
    );
  }
  return list;
}

const isRecess = (date: string) => {
  const md = date.slice(5);
  return md >= '12-20' || md <= '01-20';
};

export function calculateDeadline(input: DeadlineInput): DeadlineResult {
  const { baseDate, startKind, mode, court, capitalRecife, suspendInRecess = true, customDays = [] } = input;
  if (!isValidDate(baseDate)) throw new Error('Informe uma data válida.');
  const days = (input.doubled ? 2 : 1) * Math.floor(Number(input.days));
  if (!Number.isFinite(days) || days < 1 || days > 3650) throw new Error('Informe um número de dias válido.');

  const cache = new Map<number, Map<string, string>>();
  const reasonOf = (date: string, withRecess = suspendInRecess): string | null => {
    const year = Number(date.slice(0, 4));
    if (!cache.has(year)) {
      const map = new Map<string, string>();
      holidaysOfYear(year, court, capitalRecife).forEach((h) => map.set(h.date, h.reason));
      customDays.filter((h) => h.date.startsWith(`${year}-`)).forEach((h) => map.set(h.date, h.reason || 'Sem expediente'));
      cache.set(year, map);
    }
    const holiday = cache.get(year)!.get(date);
    if (holiday) return holiday;
    if (withRecess && isRecess(date)) return 'Recesso forense';
    const dow = weekDay(date);
    if (dow === 6) return 'Sábado';
    if (dow === 0) return 'Domingo';
    return null;
  };
  const nextBusiness = (date: string) => {
    let current = addDays(date, 1);
    while (reasonOf(current)) current = addDays(current, 1);
    return current;
  };

  const publication = startKind === 'disponibilizacao' ? nextBusiness(baseDate) : baseDate;
  const start = nextBusiness(publication);
  const skipped: NonWorkingDay[] = [];
  let current = start;
  let counted = 1;

  while (counted < days) {
    current = addDays(current, 1);
    const reason = mode === 'uteis' ? reasonOf(current) : suspendInRecess && isRecess(current) ? 'Recesso forense' : null;
    if (reason) skipped.push({ date: current, reason });
    else counted++;
  }

  let end = current;
  let movedEndFrom: string | null = null;
  if (reasonOf(end)) {
    movedEndFrom = end;
    while (reasonOf(end)) {
      const reason = reasonOf(end);
      if (reason) skipped.push({ date: end, reason });
      end = addDays(end, 1);
    }
  }

  const firstYear = Number(baseDate.slice(0, 4));
  const lastYear = Number(end.slice(0, 4));
  const calendars: CalendarUsed[] = [];
  for (let year = firstYear; year <= lastYear; year++) calendars.push({ year, source: officialCalendar(court, year)?.source || null });

  return { publication, start, end, countedDays: days, skipped, movedEndFrom, calendars };
}
